import type { BadgeTone } from '@/components/play'
import type { FeedbackKind, FeedbackPriority, FeedbackStatus } from '@/types/feedback'

export const feedbackKindLabels: Record<FeedbackKind, string> = {
  bug: 'Bug',
  feedback: 'Feedback',
  feature_request: 'Feature request',
  feature: 'Feature',
}

export const feedbackStatusLabels: Record<FeedbackStatus, string> = {
  draft: 'Draft',
  open: 'Open',
  planned: 'Planned',
  in_progress: 'In progress',
  done: 'Done',
  closed: 'Closed',
}

export const feedbackPriorityLabels: Record<Exclude<FeedbackPriority, null>, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
}

// Badge tones. Each badge renders its text label, so tone is never the only signal.
export function feedbackKindBadgeTone(kind: FeedbackKind): BadgeTone {
  switch (kind) {
    case 'bug':
      return 'error'
    case 'feature':
      return 'success'
    default:
      return 'neutral'
  }
}

export function feedbackStatusBadgeTone(status: FeedbackStatus): BadgeTone {
  switch (status) {
    case 'in_progress':
      return 'accent'
    case 'done':
      return 'success'
    case 'draft':
    case 'closed':
      return 'quiet'
    default:
      return 'neutral'
  }
}

export function feedbackPriorityBadgeTone(priority: FeedbackPriority): BadgeTone {
  switch (priority) {
    case 'critical':
      return 'error'
    case 'high':
      return 'warning'
    case 'medium':
      return 'neutral'
    default:
      return 'quiet'
  }
}

export function formatRelativeTime(value?: string | null) {
  if (!value) return 'Just now'

  const date = new Date(value)
  const diffMs = Date.now() - date.getTime()
  const diffMinutes = Math.round(diffMs / 60000)

  if (diffMinutes < 1) return 'Just now'
  if (diffMinutes < 60) return `${diffMinutes}m ago`

  const diffHours = Math.round(diffMinutes / 60)
  if (diffHours < 24) return `${diffHours}h ago`

  const diffDays = Math.round(diffHours / 24)
  if (diffDays < 7) return `${diffDays}d ago`

  return date.toLocaleDateString()
}

export function captureFeedbackContext(pathname: string) {
  const eventMatch = pathname.match(/\/events\/([^/]+)/)
  const streamMatch = pathname.match(/\/streams\/([^/]+)/)

  return {
    page_url: window.location.href,
    page_path: pathname,
    page_title: document.title,
    referrer: document.referrer,
    user_agent: navigator.userAgent,
    locale: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
    route_params: {
      event_id: eventMatch?.[1] ?? null,
      stream_id: streamMatch?.[1] ?? null,
    },
  }
}
